import React, { useEffect, useMemo, useState } from 'react'
import SlideBase from './SlideBase'
import WrappedVideoBackground from '../WrappedVideoBackground'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import { AVERAGE_SAVINGS_PERCENTAGE } from '../../config/averages'

// Get video config for this slide (static, doesn't depend on runtime data)
const SLIDE_VIDEO = WRAPPED_SLIDES.find(s => s.id === 'savings')?.video
const SLIDE_ID = 'savings' as const

function SavingsSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)
  const [animate, setAnimate] = useState(!SLIDE_VIDEO) // If no video, animate immediately

  // Start animation when video is ready (only when this is the current slide)
  useEffect(() => {
    if (SLIDE_VIDEO && isVideoReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isVideoReady, isCurrentSlide])

  // Memoize background to prevent recreation
  const videoBackground = useMemo(() => {
    if (!SLIDE_VIDEO) return undefined
    return <WrappedVideoBackground video={SLIDE_VIDEO} isActive={isCurrentSlide} />
  }, [isCurrentSlide])

  if (!data) return null

  const { stats } = data

  const isLowSavings = data.stats.totalSavings < 100

  const heading = isLowSavings ? 'In 2025, our travellers saved an average of' : 'With Luxury Escapes, you saved a total of'

  return (
    <SlideBase background={videoBackground}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">Enjoying More For Less</Caption>
            <Heading variant="heading5">
              <b>
                {heading}
              </b>
            </Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            {!isLowSavings &&
              <Heading variant="heading1alt">
                <FormatCurrency value={stats.totalSavings} currency={stats.currency} />
              </Heading>}
            {isLowSavings &&
              <Heading variant="heading1alt">
                {AVERAGE_SAVINGS_PERCENTAGE}%
              </Heading>}
            <BodyText variant="medium">
              And your next escape could save you even more.
            </BodyText>
          </Group>
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(SavingsSlide)
